// Hook usePushToTalk: gestisce MediaRecorder + permission microfono.
//
// States:
//   'idle'        → pronto, non sta facendo nulla
//   'requesting'  → richiesta permission microfono in corso (dopo prima press)
//   'recording'   → MediaRecorder attivo, sta registrando
//   'processing'  → onAudioReady è in corso (callback eseguendo upload)
//   'denied'      → permission negato
//   'unsupported' → browser non supporta MediaRecorder
//   'error'       → altro errore (es. nessun microfono)
//
// Pattern: l'utente preme & tiene → `start()`, rilascia → `stop()`.
// Il chiamante riceve onAudioReady(blob) quando lo stop è completato.

import { useCallback, useEffect, useRef, useState } from 'react';

export type PushToTalkState =
  | 'idle'
  | 'requesting'
  | 'recording'
  | 'processing'
  | 'denied'
  | 'unsupported'
  | 'error';

export interface UsePushToTalkOptions {
  onAudioReady: (blob: Blob) => Promise<void> | void;
  /** Tempo massimo di registrazione in ms (auto-stop). Default 60s. */
  maxDurationMs?: number;
  /** MIME type preferito (default 'audio/webm;codecs=opus'). */
  mimeType?: string;
  /** Disabilitato esternamente (es. encounter completed) */
  disabled?: boolean;
}

const DEFAULT_MAX_MS = 60_000;
const PREFERRED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/ogg;codecs=opus',
  'audio/ogg',
];

function pickMimeType(preferred?: string): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  if (preferred && MediaRecorder.isTypeSupported(preferred)) return preferred;
  for (const m of PREFERRED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return undefined;
}

export function usePushToTalk(opts: UsePushToTalkOptions) {
  const { onAudioReady, maxDurationMs = DEFAULT_MAX_MS, mimeType, disabled = false } = opts;

  const [state, setState] = useState<PushToTalkState>(() => {
    if (typeof MediaRecorder === 'undefined') return 'unsupported';
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia)
      return 'unsupported';
    return 'idle';
  });
  const [recordingMs, setRecordingMs] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const startedAtRef = useRef<number>(0);
  const tickerRef = useRef<number | null>(null);
  const autoStopRef = useRef<number | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAndCleanup();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    };
  }, []);

  const stopAndCleanup = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try {
        recorderRef.current.stop();
      } catch {
        /* ignored */
      }
    }
    recorderRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (tickerRef.current !== null) {
      window.clearInterval(tickerRef.current);
      tickerRef.current = null;
    }
    if (autoStopRef.current !== null) {
      window.clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
  }, []);

  const start = useCallback(async () => {
    if (disabled || state !== 'idle') return;
    setError(null);
    setState('requesting');

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const e = err as DOMException;
      if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
        setState('denied');
      } else if (e.name === 'NotFoundError') {
        setState('error');
        setError('NO_MICROPHONE');
      } else {
        setState('error');
        setError(e.message || 'GET_USER_MEDIA_FAILED');
      }
      return;
    }

    const chosenMime = pickMimeType(mimeType);
    let recorder: MediaRecorder;
    try {
      recorder = chosenMime
        ? new MediaRecorder(stream, { mimeType: chosenMime })
        : new MediaRecorder(stream);
    } catch (err) {
      stream.getTracks().forEach((t) => t.stop());
      setState('error');
      setError((err as Error).message);
      return;
    }

    chunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = async () => {
      const finalMime = recorder.mimeType || chosenMime || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: finalMime });
      chunksRef.current = [];
      // tracks già fermate da stopAndCleanup; doppio stop è no-op
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;

      setState('processing');
      try {
        await onAudioReady(blob);
      } catch (err) {
        setError((err as Error).message);
        setState('error');
        return;
      }
      setState('idle');
      setRecordingMs(0);
    };
    recorder.onerror = (e: Event) => {
      const ev = e as Event & { error?: Error };
      setState('error');
      setError(ev.error?.message ?? 'RECORDER_ERROR');
    };

    recorderRef.current = recorder;
    streamRef.current = stream;
    chunksRef.current = [];
    startedAtRef.current = Date.now();
    setState('recording');
    setRecordingMs(0);
    recorder.start();

    // Ticker per UI
    tickerRef.current = window.setInterval(() => {
      setRecordingMs(Date.now() - startedAtRef.current);
    }, 100);

    // Auto-stop dopo maxDuration
    autoStopRef.current = window.setTimeout(() => {
      stop();
    }, maxDurationMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, state, mimeType, maxDurationMs, onAudioReady]);

  const stop = useCallback(() => {
    if (state !== 'recording') return;
    if (tickerRef.current !== null) {
      window.clearInterval(tickerRef.current);
      tickerRef.current = null;
    }
    if (autoStopRef.current !== null) {
      window.clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
    const r = recorderRef.current;
    if (r && r.state !== 'inactive') {
      try {
        r.stop();
      } catch {
        /* ignored */
      }
    }
    // state passa a 'processing' dentro onstop
  }, [state]);

  /** Reset hard (chiamato dopo errore irreversibile per ritentare). */
  const reset = useCallback(() => {
    stopAndCleanup();
    setError(null);
    setState(typeof MediaRecorder === 'undefined' ? 'unsupported' : 'idle');
    setRecordingMs(0);
  }, [stopAndCleanup]);

  return {
    state,
    recordingMs,
    error,
    start,
    stop,
    reset,
    isReady: state === 'idle',
    isRecording: state === 'recording',
    isProcessing: state === 'processing',
  };
}
